import { AnimatePresence, motion } from 'framer-motion';
import numeral from 'numeral';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@feathr/components';

import { MIN_REACHABLE_AUDIENCE } from './AdWizardAudienceStep.utils';

/** Help Center article on the microtargeting minimum and how to broaden a small audience. */
const MICROTARGETING_HELP_URL =
  'https://help.feathr.co/hc/en-us/articles/41495022488471-Microtargeting-Policy-Update';

interface IBelowThresholdAlertProps {
  show: boolean;
  name: string;
  description: ReactNode;
  children?: ReactNode;
}

/** Fade-animated below-threshold warning alert with the shared help link; used by all three cases. */
function BelowThresholdAlert({
  show,
  name,
  description,
  children,
}: Readonly<IBelowThresholdAlertProps>): JSX.Element {
  const { t } = useTranslation();
  const minimum = numeral(MIN_REACHABLE_AUDIENCE).format('0,0');
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <Alert
            description={description}
            name={name}
            title={t('Your audience is below the {{minimum}} person minimum', { minimum })}
            type={'warning'}
          >
            {children}
            <a href={MICROTARGETING_HELP_URL} rel={'noopener noreferrer'} target={'_blank'}>
              {t('Learn how to grow your audience')}
            </a>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default BelowThresholdAlert;
export type { IBelowThresholdAlertProps };
